/**
 * M18 (2026-06-17): Consultor visualiza sua própria classificação no
 * Ranking de Estrelas. Sem acesso aos dados detalhados de outros consultores.
 */
import { useEffect, useState } from "react";
import { Award, Star, TrendingUp, Calendar, FileText, User } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { StarRating } from "@/components/StarRating";
import { api, formatDateBR } from "@/lib/api";

const CRITERIA_LABELS = {
  pontualidade: "Pontualidade das entregas",
  gramatica: "Padrão de redação (gramática)",
  legistica: "Legística e técnica legislativa",
  dominio_tema: "Domínio do tema",
  qualidade_consolidacao: "Qualidade da consolidação",
};

function StatCard({ icon: Icon, label, value, hint, accent, testId }) {
  return (
    <Card className="card-soft" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
            <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
            {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
          </div>
          <div className={`p-2 rounded-md ${accent}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RankingPanel({ data, periodLabel }) {
  if (!data) return <Skeleton className="h-64" />;
  const {
    avg, total_ratings, breakdown, position, total_consultants, history,
  } = data;

  return (
    <div className="space-y-6" data-testid={`ranking-panel-${periodLabel}`}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Star}
          label="Média geral"
          value={avg.toFixed(1)}
          hint={`de 5,0 estrelas`}
          accent="bg-amber-50 text-amber-700"
          testId="rank-avg"
        />
        <StatCard
          icon={Award}
          label="Sua posição"
          value={position ? `#${position}` : "—"}
          hint={total_consultants ? `entre ${total_consultants} consultores avaliados` : "sem ranking neste período"}
          accent="bg-emerald-50 text-emerald-700"
          testId="rank-position"
        />
        <StatCard
          icon={FileText}
          label="Avaliações recebidas"
          value={total_ratings}
          hint="entregas avaliadas"
          accent="bg-blue-50 text-blue-700"
          testId="rank-count"
        />
        <StatCard
          icon={TrendingUp}
          label="Média do melhor critério"
          value={Math.max(...Object.values(breakdown)).toFixed(1)}
          hint="destaque"
          accent="bg-sky-50 text-sky-700"
          testId="rank-best"
        />
      </div>

      {total_ratings === 0 ? (
        <Card className="card-soft" data-testid="ranking-empty">
          <CardContent className="py-12 text-center">
            <Award className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <div className="text-sm font-medium text-slate-700">
              Ainda não há avaliações neste período
            </div>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              Suas avaliações começam a aparecer aqui após o Controle Jurídico
              e a Diretora avaliarem seus documentos entregues.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-midnight" />
                Desempenho por critério
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(CRITERIA_LABELS).map(([key, label]) => {
                  const val = breakdown[key] ?? 0;
                  return (
                    <div key={key} data-testid={`criterion-${key}`}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-slate-700">{label}</span>
                        <div className="flex items-center gap-2">
                          <StarRating value={Math.round(val)} readOnly size="sm" />
                          <span className="text-sm font-semibold text-slate-900 w-10 text-right">
                            {val.toFixed(1)}
                          </span>
                        </div>
                      </div>
                      <Progress value={(val / 5) * 100} className="h-1.5" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-4 h-4 text-midnight" />
                Histórico de avaliações
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ul>
                {history.map((h) => (
                  <li
                    key={h.id}
                    className="p-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60"
                    data-testid={`rank-history-${h.id}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                          <User className="w-3.5 h-3.5 text-slate-400" />
                          {h.rated_by || "—"}
                          <Badge variant="outline" className="text-[10px]">
                            {h.role}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {formatDateBR(h.rated_at)}
                          {h.comment && (
                            <span className="ml-2 italic">— &ldquo;{h.comment}&rdquo;</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <StarRating value={Math.round(h.avg)} readOnly size="sm" />
                        <span className="text-sm font-semibold text-slate-900">{h.avg.toFixed(1)}</span>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-x-3 gap-y-1 text-[11px] text-slate-500">
                      {Object.entries(CRITERIA_LABELS).map(([key, label]) => (
                        <div key={key} className="flex justify-between border-r border-slate-100 pr-2 last:border-0">
                          <span className="truncate">{label.split(" ")[0]}</span>
                          <span className="font-semibold text-slate-700">
                            {h.criteria[key] ?? "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default function MyRanking() {
  const [period, setPeriod] = useState("all");
  const [dataAll, setDataAll] = useState(null);
  const [dataMonth, setDataMonth] = useState(null);

  useEffect(() => {
    api.get("/ratings/me", { params: { period: "all" } })
      .then(({ data }) => setDataAll(data))
      .catch(() => setDataAll(null));
    api.get("/ratings/me", { params: { period: "month" } })
      .then(({ data }) => setDataMonth(data))
      .catch(() => setDataMonth(null));
  }, []);

  return (
    <div className="space-y-6" data-testid="my-ranking-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-600" />
          Meu Ranking
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Acompanhe sua avaliação por critério e evolução ao longo do tempo.
          Suas notas são calculadas com base nas avaliações do Controle Jurídico e da Diretora.
        </p>
      </div>

      <Tabs value={period} onValueChange={setPeriod} className="space-y-6">
        <TabsList>
          <TabsTrigger value="month" data-testid="tab-ranking-month">Mês atual</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-ranking-all">Todo o histórico</TabsTrigger>
        </TabsList>
        <TabsContent value="month">
          <RankingPanel data={dataMonth} periodLabel="month" />
        </TabsContent>
        <TabsContent value="all">
          <RankingPanel data={dataAll} periodLabel="all" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
